'use client';

import { useState, useRef, useEffect, RefObject } from 'react';

// Which edges of a horizontally scrollable element still have hidden content.
export interface ScrollFadeState {
  start: boolean; // content scrolled past the left edge (more to reveal by scrolling back)
  end: boolean; // content beyond the right edge (more to reveal by scrolling forward)
}

// Tracks horizontal overflow of an element so callers can fade the start/end edges. Client only.
// Content is static (CMS-rendered), so we only react to scrolling and to size changes
// (viewport/container resize, plus initial layout) — no DOM-mutation watching needed.
export function useScrollFade<T extends HTMLElement>(): [RefObject<T | null>, ScrollFadeState] {
  const ref = useRef<T>(null);
  const [fade, setFade] = useState<ScrollFadeState>({ start: false, end: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Recompute which edges have hidden content (1px tolerance avoids sub-pixel flicker)
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      setFade({ start: scrollLeft > 1, end: scrollLeft < maxScroll - 1 });
    };

    // ResizeObserver fires on observe (initial measure) and on any resize.
    const observer = new ResizeObserver(update);
    observer.observe(el);
    el.addEventListener('scroll', update, { passive: true });

    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  return [ref, fade];
}
